import React from 'react';
import { BookOpen, FileText, Mic, ImageIcon } from 'lucide-react';

const WAVE_HEIGHTS = [14, 22, 18, 28, 16, 24, 20, 26];

/**
 * Cinematic hero visual — floating archive cards, waveform, tree lines, keepsake book.
 * Pure CSS/SVG; no external assets.
 */
export function CinematicHeroVisual() {
  return (
    <div
      className="relative mx-auto aspect-[4/3] w-full max-w-xl lg:max-w-none lg:aspect-auto lg:min-h-[28rem]"
      aria-hidden
      data-testid="cinematic-hero-visual"
    >
      {/* Family tree connection lines */}
      <svg
        className="absolute inset-0 h-full w-full opacity-80"
        viewBox="0 0 480 360"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="240" cy="72" r="5" fill="hsl(43 48% 58% / 0.5)" />
        <circle cx="140" cy="160" r="4" fill="hsl(42 28% 88% / 0.35)" />
        <circle cx="340" cy="160" r="4" fill="hsl(42 28% 88% / 0.35)" />
        <circle cx="240" cy="240" r="4" fill="hsl(42 28% 88% / 0.3)" />
        <path
          className="cine-tree-line"
          d="M240 77 L240 130 M240 130 L140 155 M240 130 L340 155 M240 130 L240 235"
          stroke="hsl(43 48% 58%)"
          strokeWidth="1"
          strokeOpacity="0.35"
        />
        <path
          className="cine-tree-line"
          style={{ animationDelay: '1.2s' }}
          d="M140 165 L190 200 M340 165 L290 200 M240 245 L240 280"
          stroke="hsl(43 40% 50%)"
          strokeWidth="0.75"
          strokeOpacity="0.22"
        />
      </svg>

      {/* Central glow */}
      <div className="absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(43_48%_58%/_0.08)] blur-3xl" />

      {/* Photo memory card */}
      <div className="cine-float-a absolute left-[4%] top-[18%] w-[38%] max-w-[11rem] sm:left-[8%] sm:top-[14%]">
        <div className="cine-glass overflow-hidden rounded-2xl p-2.5">
          <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-gradient-to-br from-[hsl(38_18%_22%)] via-[hsl(152_18%_16%)] to-[hsl(24_12%_14%)]">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 30% 25%, hsl(43 45% 65% / 0.25), transparent 50%)',
              }}
            />
            <ImageIcon className="absolute bottom-2 right-2 h-4 w-4 text-[hsl(42_28%_88%/_0.4)]" />
          </div>
          <p className="mt-2 px-1 text-[10px] tracking-wide text-[hsl(38_16%_62%)]">Family photo · 1962</p>
        </div>
      </div>

      {/* Voice memory card */}
      <div className="cine-float-b absolute right-[2%] top-[8%] w-[42%] max-w-[12.5rem] sm:right-[6%] sm:top-[6%]">
        <div className="cine-glass rounded-2xl p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(152_28%_18%)] text-[hsl(42_30%_96%)]">
              <Mic className="h-3.5 w-3.5" />
            </span>
            <div>
              <p className="text-[11px] font-medium text-[hsl(42_28%_88%)]">Voice memory</p>
              <p className="text-[9px] text-[hsl(38_16%_55%)]">Preserved · private</p>
            </div>
          </div>
          <div className="flex h-10 items-end justify-center gap-[3px] px-1">
            {WAVE_HEIGHTS.map((h, i) => (
              <span
                key={i}
                className="cine-wave-bar w-[3px] rounded-full bg-gradient-to-t from-[hsl(43_40%_42%)] to-[hsl(43_55%_62%)]"
                style={{ height: `${h}px` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Document vault card */}
      <div className="cine-float-c absolute bottom-[12%] left-[6%] w-[36%] max-w-[10rem] sm:bottom-[16%] sm:left-[12%]">
        <div className="cine-glass flex items-center gap-2.5 rounded-xl px-3 py-2.5">
          <FileText className="h-5 w-5 shrink-0 text-[hsl(43_48%_58%)]" />
          <div className="min-w-0">
            <p className="truncate text-[10px] font-medium text-[hsl(42_28%_88%)]">Document vault</p>
            <p className="text-[9px] text-[hsl(38_16%_55%)]">Letters · certificates</p>
          </div>
        </div>
      </div>

      {/* Keepsake book export */}
      <div className="cine-float-a absolute bottom-[8%] right-[4%] w-[44%] max-w-[13rem] sm:right-[10%] sm:bottom-[10%]">
        <div className="cine-glass relative overflow-hidden rounded-2xl p-3">
          <div className="cine-book-shine pointer-events-none absolute inset-0" />
          <div className="flex gap-3">
            <div className="relative h-16 w-12 shrink-0 rounded-r-md rounded-l-sm border border-[hsl(43_30%_40%/_0.35)] bg-gradient-to-br from-[hsl(152_22%_14%)] to-[hsl(24_10%_10%)] shadow-lg">
              <div className="absolute inset-y-1 left-0 w-[2px] bg-[hsl(43_48%_58%/_0.35)]" />
              <BookOpen className="absolute bottom-1.5 right-1 h-3 w-3 text-[hsl(43_48%_58%/_0.7)]" />
            </div>
            <div className="min-w-0 pt-0.5">
              <p className="text-[11px] font-medium text-[hsl(42_28%_88%)]">Keepsake export</p>
              <p className="mt-0.5 text-[9px] leading-snug text-[hsl(38_16%_55%)]">
                Stories &amp; voices bound for generations
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Story studio hint — center */}
      <div className="absolute left-1/2 top-[42%] w-[46%] max-w-[14rem] -translate-x-1/2 sm:top-[38%]">
        <div className="cine-glass rounded-xl px-3 py-2.5 text-center">
          <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-[hsl(43_40%_50%)]">Story studio</p>
          <p className="mt-1 font-serif text-xs italic leading-snug text-[hsl(42_28%_88%/_0.85)]">
            &ldquo;She told it best by the kitchen window&hellip;&rdquo;
          </p>
        </div>
      </div>
    </div>
  );
}

export default CinematicHeroVisual;
